import { Injectable } from '@angular/core';
import { LocalizationService } from '../../../../shared/services';
import { LocationService } from 'src/app/core/services/global';
import {
  createWandifyProfileUrlPattern,
  linkedinProfileUrlPattern,
} from '../../../../shared/utilities';
import {
  AiSearchAdjustmentData,
  AiSearchAdjustmentDto,
  AiSearchDto,
  SuggestedFilterChip,
  SuggestedFilterGroupData,
  SuggestedFiltersState,
} from '../../../../shared/models';
import { FindSimilarModalOutput, FindSimilarModalResult } from '../components';

@Injectable()
export class AiSearchHandlerService {
  private cachedWfUrlPattern: RegExp | null = null;
  constructor(
    private locationService: LocationService,
    private localizationService: LocalizationService,
  ) {}

  getProfileUrlPattern(): RegExp {
    if (!this.cachedWfUrlPattern) {
      const url = this.locationService.getAppBaseUrl();
      const hostMatch = url.match(/^(https?:\/\/[^/]+)/);
      const wfHost = hostMatch ? hostMatch[1] : 'https://wandify.io';

      this.cachedWfUrlPattern = createWandifyProfileUrlPattern(wfHost);
    }

    return this.cachedWfUrlPattern;
  }

  isValidLinkedinUrl(url: string): boolean {
    return linkedinProfileUrlPattern.test(url);
  }

  isValidWandifyUrl(url: string): boolean {
    const pattern = this.getProfileUrlPattern();
    return pattern?.test(url) || false;
  }

  isFindSimilarSubmitResult(result: FindSimilarModalResult): result is FindSimilarModalOutput {
    return result !== null && 'linkedinUrls' in result;
  }

  partitionAdjustmentsForModal(
    adjustments: AiSearchAdjustmentDto[],
    similarDataToPersist: AiSearchAdjustmentData[],
  ): {
    similarAdjustments: AiSearchAdjustmentData[];
    likeDislikeAdjustments: AiSearchAdjustmentData[];
  } {
    return adjustments.reduce<{
      similarAdjustments: AiSearchAdjustmentData[];
      likeDislikeAdjustments: AiSearchAdjustmentData[];
    }>(
      (acc, { url, type, fullName, headline, status, docId }) => {
        const adjustmentData: AiSearchAdjustmentData = { url, type, fullName, headline, docId };

        if (status !== 'ACTIVE') {
          return acc;
        }

        if (type === 'similar' && similarDataToPersist.some((adj) => adj.url === url)) {
          acc.similarAdjustments.push(adjustmentData);
        } else if (type === 'like' || type === 'dislike') {
          acc.likeDislikeAdjustments.push(adjustmentData);
        }

        return acc;
      },
      { similarAdjustments: [], likeDislikeAdjustments: [] },
    );
  }

  createSimilarAdjustmentsToPersist(
    allAdjustments: AiSearchAdjustmentDto[],
  ): AiSearchAdjustmentData[] {
    return allAdjustments.reduce<AiSearchAdjustmentData[]>(
      (acc, { url, type, fullName, headline, docId }) => {
        if (type === 'similar') {
          acc.push({ url, type, fullName, headline, docId });
        }
        return acc;
      },
      [],
    );
  }

  buildSuggestedFilters(
    dto: AiSearchDto,
    previousState: SuggestedFiltersState | null = null,
  ): SuggestedFiltersState | null {
    if (!dto) {
      return null;
    }

    // Carry over the "applied" state from the previous suggestions so chips the
    // user already added stay marked (and disabled) after an AI search update —
    // otherwise the same chip reappears clickable and re-applying it duplicates
    // the underlying filter. Matched by source+label, not the index-based id.
    const appliedKeys = this.collectAppliedKeys(previousState);
    const groups: SuggestedFilterGroupData[] = [];

    if (dto.locations?.length) {
      groups.push({
        type: 'locations',
        labelKey: 'SUGGESTED_FILTERS.GROUP.LOCATIONS',
        mainChips: dto.locations.map((loc, i) => this.buildChip('locations', i, loc, appliedKeys)),
      });
    }

    if (dto.positions?.length) {
      groups.push({
        type: 'positions',
        labelKey: 'SUGGESTED_FILTERS.GROUP.POSITIONS',
        mainChips: dto.positions.map((pos, i) => this.buildChip('positions', i, pos, appliedKeys)),
      });
    }

    const hasMainSkills = !!dto.mainSkills?.length;
    const hasSkills = !!dto.skills?.length;

    if (hasMainSkills || hasSkills) {
      groups.push({
        type: 'skills',
        labelKey: 'SUGGESTED_FILTERS.GROUP.SKILLS',
        mainChips: hasMainSkills
          ? dto.mainSkills.map((skill, i) => this.buildChip('mainSkills', i, skill, appliedKeys))
          : [],
        additionalChips: hasSkills
          ? dto.skills.map((skill, i) => this.buildChip('skills', i, skill, appliedKeys, true))
          : undefined,
      });
    }

    const hasMainKeywords = !!dto.mainKeywords?.length;
    const hasKeywords = !!dto.keywords?.length;

    if (hasMainKeywords || hasKeywords) {
      groups.push({
        type: 'keywords',
        labelKey: 'SUGGESTED_FILTERS.GROUP.KEYWORDS',
        mainChips: hasMainKeywords
          ? dto.mainKeywords.map((kw, i) => this.buildChip('mainKeywords', i, kw, appliedKeys))
          : [],
        additionalChips: hasKeywords
          ? dto.keywords.map((kw, i) => this.buildChip('keywords', i, kw, appliedKeys))
          : undefined,
      });
    }

    if (dto.experienceFrom > 0) {
      groups.push({
        type: 'experience',
        labelKey: 'SUGGESTED_FILTERS.GROUP.EXPERIENCE',
        mainChips: [this.buildChip('experience', 0, `${dto.experienceFrom}+ years`, appliedKeys)],
      });
    }

    if (!groups.length) {
      return null;
    }

    return { groups, visible: true };
  }

  partitionSimilarAdjustmentsForPersistence(
    adjustmentsData: AiSearchAdjustmentData[],
    allAdjustments: AiSearchAdjustmentDto[],
  ): {
    linkedinUrlsToPersist: string[];
    wfUrlsToPersist: string[];
  } {
    const baseUrl = this.locationService.getAppBaseUrl();
    const locale = this.localizationService.getSystemLocale();

    return adjustmentsData.reduce<{ linkedinUrlsToPersist: string[]; wfUrlsToPersist: string[] }>(
      (acc, { url }) => {
        const matchedDto = allAdjustments.find((adj) => adj.url === url);

        if (matchedDto) {
          if (matchedDto.similarType === 'linkedin') {
            acc.linkedinUrlsToPersist.push(matchedDto.url);
          } else if (matchedDto.similarType === 'wf_url') {
            const wfUrl = `${baseUrl}/${locale}/profile/${matchedDto.profileId}`;
            acc.wfUrlsToPersist.push(wfUrl);
          }
        } else {
          if (this.isValidLinkedinUrl(url)) {
            acc.linkedinUrlsToPersist.push(url);
          } else if (this.isValidWandifyUrl(url)) {
            acc.wfUrlsToPersist.push(url);
          }
        }

        return acc;
      },
      { linkedinUrlsToPersist: [], wfUrlsToPersist: [] },
    );
  }

  private buildChip(
    source: SuggestedFilterChip['source'],
    index: number,
    label: string,
    appliedKeys: Set<string>,
    isAdditional = false,
  ): SuggestedFilterChip {
    return {
      id: `${source}-${index}`,
      source,
      label,
      isAdditional,
      applied: appliedKeys.has(this.getChipKey(source, label)),
    };
  }

  private collectAppliedKeys(state: SuggestedFiltersState | null): Set<string> {
    const keys = new Set<string>();

    state?.groups.forEach((group) => {
      [...(group.mainChips ?? []), ...(group.additionalChips ?? [])].forEach((chip) => {
        if (chip.applied) {
          keys.add(this.getChipKey(chip.source, chip.label));
        }
      });
    });

    return keys;
  }

  private getChipKey(source: SuggestedFilterChip['source'], label: string): string {
    return `${source}::${label}`;
  }
}
